/**
 * Communities without an API (C13): Facebook groups and WhatsApp communities. The platform only
 * keeps their invitation links and prepares the content; a person copies it or opens WhatsApp
 * (wa.me) and sends it herself. Nothing is automated (Meta forbids it for groups).
 */

import {
  type CommunityContent,
  type CommunityLink,
  type CommunityLinkKindEnum,
  useSocialCommunitiesCreate,
  useSocialCommunitiesDestroy,
  useSocialCommunitiesList,
  useSocialCommunityContentsCreate,
  useSocialCommunityContentsDestroy,
  useSocialCommunityContentsList,
  useSocialCommunityContentsSharedCreate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, SelectField, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { Copy, Share2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { copyText, useRefreshSocial, useSocialRoles } from "./shared";

const KINDS: CommunityLinkKindEnum[] = ["whatsapp_community", "whatsapp_group", "whatsapp_channel", "facebook_group", "other"];

function CopyButton({ text, label, onCopied }: { text: string; label: string; onCopied?: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <>
      <Button
        variant="secondary"
        icon={<Copy size={16} aria-hidden="true" />}
        aria-label={label}
        onClick={() =>
          void copyText(text).then((ok) => {
            setCopied(ok);
            if (ok) onCopied?.();
          })
        }
      >
        {t("social.communities.copy")}
      </Button>
      {copied ? <span role="status">{t("social.communities.copied")}</span> : null}
    </>
  );
}

function ShareLink({ href, label, onClick }: { href: string; label: string; onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <a className="ao-button ao-button--whatsapp" href={href} target="_blank" rel="noopener noreferrer" aria-label={label} onClick={onClick}>
      <Share2 size={16} aria-hidden="true" /> {t("social.communities.shareWhatsapp")}
    </a>
  );
}

function LinkForm() {
  const { t } = useTranslation();
  const refresh = useRefreshSocial();
  const [form, setForm] = useState({ name: "", kind: "whatsapp_community" as CommunityLinkKindEnum, url: "", notes: "" });
  const create = useSocialCommunitiesCreate({
    mutation: {
      onSuccess: () => {
        setForm({ name: "", kind: "whatsapp_community", url: "", notes: "" });
        refresh();
      },
    },
  });
  const errors = fieldErrors(create.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate({ data: { ...form, name: form.name.trim(), url: form.url.trim() } });
  };
  return (
    <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("social.communities.newLink")}>
      <div className="form-grid">
        <TextField label={t("social.communities.name")} value={form.name} maxLength={120} error={errors.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <SelectField label={t("social.communities.kind")} value={form.kind} options={KINDS.map((k) => ({ value: k, label: t(`social.communities.kinds.${k}`) }))} onChange={(e) => setForm({ ...form, kind: e.target.value as CommunityLinkKindEnum })} />
        <TextField type="url" label={t("social.communities.url")} hint={t("social.communities.urlHint")} value={form.url} error={errors.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        <TextField label={t("social.communities.notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
      <div className="row">
        <Button type="submit" disabled={create.isPending}>
          {t("social.communities.addLink")}
        </Button>
      </div>
    </form>
  );
}

function LinkRow({ link }: { link: CommunityLink }) {
  const { t } = useTranslation();
  const { canWork } = useSocialRoles();
  const refresh = useRefreshSocial();
  const remove = useSocialCommunitiesDestroy({ mutation: { onSettled: refresh } });
  return (
    <li className="social-comment">
      <div className="row between">
        <strong>{link.name}</strong>
        <Badge>{t(`social.communities.kinds.${link.kind}`)}</Badge>
      </div>
      <a className="link" href={link.url} target="_blank" rel="noopener noreferrer">
        {link.url}
      </a>
      {link.notes ? <p className="muted">{link.notes}</p> : null}
      <div className="row">
        <CopyButton text={link.url} label={t("social.communities.copyLink", { name: link.name })} />
        <ShareLink href={link.share_url} label={t("social.communities.shareLink", { name: link.name })} />
        {canWork ? (
          <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate({ id: link.id })}>
            {t("common.remove")}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function ContentForm({ links }: { links: CommunityLink[] }) {
  const { t } = useTranslation();
  const refresh = useRefreshSocial();
  const empty = { title: "", text: "", mediaUrl: "", links: [] as string[] };
  const [form, setForm] = useState(empty);
  const create = useSocialCommunityContentsCreate({
    mutation: {
      onSuccess: () => {
        setForm(empty);
        refresh();
      },
    },
  });
  const errors = fieldErrors(create.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate({ data: { title: form.title.trim(), text: form.text.trim(), media_url: form.mediaUrl.trim(), links: form.links } });
  };
  return (
    <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("social.communities.newContent")}>
      <TextField label={t("social.communities.contentTitle")} value={form.title} maxLength={160} error={errors.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <TextAreaField label={t("social.communities.contentText")} value={form.text} rows={4} error={errors.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
      <TextField type="url" label={t("social.communities.mediaUrl")} hint={t("social.communities.mediaUrlHint")} value={form.mediaUrl} error={errors.media_url} onChange={(e) => setForm({ ...form, mediaUrl: e.target.value })} />
      {links.length ? (
        <fieldset className="plain-fieldset stack">
          <legend>{t("social.communities.targets")}</legend>
          {links.map((link) => (
            <SoftToggle
              key={link.id}
              label={link.name}
              checked={form.links.includes(link.id)}
              onChange={(on) => setForm({ ...form, links: on ? [...form.links, link.id] : form.links.filter((id) => id !== link.id) })}
            />
          ))}
        </fieldset>
      ) : null}
      <div className="row">
        <Button type="submit" disabled={create.isPending}>
          {t("social.communities.prepare")}
        </Button>
      </div>
    </form>
  );
}

function ContentRow({ content, links }: { content: CommunityContent; links: CommunityLink[] }) {
  const { t, i18n } = useTranslation();
  const { canWork } = useSocialRoles();
  const refresh = useRefreshSocial();
  const shared = useSocialCommunityContentsSharedCreate({ mutation: { onSettled: refresh } });
  const remove = useSocialCommunityContentsDestroy({ mutation: { onSettled: refresh } });
  const count = () => shared.mutate({ id: content.id });
  const targets = links.filter((link) => content.link_ids.includes(link.id));
  return (
    <li className="social-comment">
      <div className="row between">
        <strong>{content.title}</strong>
        <small className="muted">
          {t("social.communities.sharedCount", { count: content.shared_count })}
          {content.last_shared_at ? ` · ${new Date(content.last_shared_at).toLocaleDateString(i18n.language === "en" ? "en-GB" : i18n.language, { timeZone: "Africa/Douala" })}` : ""}
        </small>
      </div>
      <p className="social-comment__text">{content.text}</p>
      {content.media_link ? (
        <a className="link" href={content.media_link} target="_blank" rel="noopener noreferrer">
          {t("social.communities.openMedia")}
        </a>
      ) : null}
      {targets.length ? <p className="muted">{t("social.communities.for", { names: targets.map((l) => l.name).join(", ") })}</p> : null}
      <div className="row">
        <CopyButton text={content.copy_text} label={t("social.communities.copyContent", { title: content.title })} onCopied={count} />
        <ShareLink href={content.share_url} label={t("social.communities.shareContent", { title: content.title })} onClick={count} />
        {canWork ? (
          <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate({ id: content.id })}>
            {t("common.remove")}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export function CommunitiesTab() {
  const { t } = useTranslation();
  const { canWork } = useSocialRoles();
  const linkList = useSocialCommunitiesList();
  const contentList = useSocialCommunityContentsList();
  const links = linkList.data?.status === 200 ? linkList.data.data.results : undefined;
  const contents = contentList.data?.status === 200 ? contentList.data.data.results : undefined;
  return (
    <div className="stack">
      <p className="notice-warning ao-glass ao-card">{t("social.communities.rule")}</p>
      <div className="bento">
      <GlassCard as="section" className="stack bento__half" aria-labelledby="social-links-title">
        <h2 id="social-links-title">{t("social.communities.linksTitle")}</h2>
        {linkList.isPending ? <Skeleton /> : null}
        {links && !links.length ? <p className="muted">{t("social.communities.noLinks")}</p> : null}
        {links?.length ? (
          <ul className="plain-list stack">
            {links.map((link) => (
              <LinkRow key={link.id} link={link} />
            ))}
          </ul>
        ) : null}
        {canWork ? <LinkForm /> : null}
      </GlassCard>
      <GlassCard as="section" className="stack bento__half" aria-labelledby="social-contents-title">
        <h2 id="social-contents-title">{t("social.communities.contentsTitle")}</h2>
        <p className="muted">{t("social.communities.contentsIntro")}</p>
        {contentList.isPending ? <Skeleton /> : null}
        {contents && !contents.length ? <p className="muted">{t("social.communities.noContents")}</p> : null}
        {contents?.length ? (
          <ul className="plain-list stack">
            {contents.map((content) => (
              <ContentRow key={content.id} content={content} links={links ?? []} />
            ))}
          </ul>
        ) : null}
        {canWork ? <ContentForm links={links ?? []} /> : null}
      </GlassCard>
      </div>
    </div>
  );
}
